import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, RotateCcw, Youtube, Plus, Trash2, ExternalLink } from "lucide-react";
import AdminShell from "@/components/AdminShell";
import api, { errMsg } from "@/lib/api";

const DEMO_VIDEO_CATEGORIES = [
    "How It Works",
    "Onboarding Demo",
    "Success Story",
    "Feature Walkthrough",
    "Product Overview",
];

const CATEGORY_LABEL = {
    company: "Company",
    services: "Services",
    support: "Support & Help",
    legal: "Legal",
    technical: "Trust & Technical",
    help: "Help hub",
};

/** Super Admin — Marketing CMS Pages + Demo Videos manager. */
export default function SuperAdminMarketingPages() {
    const [pages, setPages] = useState([]);
    const [selKey, setSelKey] = useState(null);
    const [draft, setDraft] = useState(null);
    const [busy, setBusy] = useState(false);
    const [videos, setVideos] = useState([]);
    const [vDraft, setVDraft] = useState({
        title: "", youtube_url: "", description: "",
        category: DEMO_VIDEO_CATEGORIES[0], sort_order: 0, published: true,
    });
    const [vBusy, setVBusy] = useState(false);

    useEffect(() => { loadPages(); loadVideos(); }, []);

    async function loadPages() {
        try {
            const r = await api.get("/super-admin/pages");
            setPages(r.data.items || []);
        } catch (e) { toast.error(errMsg(e, "Failed to load pages")); }
    }
    async function loadVideos() {
        try {
            const r = await api.get("/super-admin/demo-videos");
            setVideos(r.data.items || []);
        } catch (e) { toast.error(errMsg(e, "Failed to load videos")); }
    }

    async function pickPage(key) {
        setSelKey(key);
        try {
            const r = await api.get(`/super-admin/pages/${key}`);
            setDraft(r.data);
        } catch (e) { toast.error(errMsg(e, "Failed to load page")); }
    }

    async function savePage() {
        if (!draft) return;
        setBusy(true);
        try {
            await api.put(`/super-admin/pages/${draft.key}`, {
                title: draft.title, meta_description: draft.meta_description,
                body_markdown: draft.body_markdown, published: !!draft.published,
                sort_order: draft.sort_order || 0,
            });
            toast.success("Page saved");
            loadPages();
        } catch (e) { toast.error(errMsg(e, "Save failed")); }
        finally { setBusy(false); }
    }

    async function restorePage() {
        if (!draft) return;
        if (!window.confirm(`Restore "${draft.title}" to seeded defaults? Your current edits will be replaced.`)) return;
        setBusy(true);
        try {
            const r = await api.post(`/super-admin/pages/${draft.key}/restore-defaults`);
            setDraft(r.data.page);
            toast.success("Restored to defaults");
            loadPages();
        } catch (e) { toast.error(errMsg(e, "Restore failed")); }
        finally { setBusy(false); }
    }

    async function createVideo() {
        if (!vDraft.title.trim() || !vDraft.youtube_url.trim()) {
            toast.error("Title and YouTube URL are required"); return;
        }
        setVBusy(true);
        try {
            await api.post("/super-admin/demo-videos", vDraft);
            toast.success("Video added");
            setVDraft({ title: "", youtube_url: "", description: "",
                          category: DEMO_VIDEO_CATEGORIES[0], sort_order: 0, published: true });
            loadVideos();
        } catch (e) { toast.error(errMsg(e, "Create failed")); }
        finally { setVBusy(false); }
    }

    async function updateVideo(id, patch) {
        try {
            await api.put(`/super-admin/demo-videos/${id}`, patch);
            loadVideos();
        } catch (e) { toast.error(errMsg(e, "Update failed")); }
    }

    async function deleteVideo(id) {
        if (!window.confirm("Delete this demo video?")) return;
        try {
            await api.delete(`/super-admin/demo-videos/${id}`);
            toast.success("Deleted");
            loadVideos();
        } catch (e) { toast.error(errMsg(e, "Delete failed")); }
    }

    const grouped = useMemo(() => {
        const g = {};
        for (const p of pages) {
            const cat = p.category || "other";
            (g[cat] = g[cat] || []).push(p);
        }
        return g;
    }, [pages]);

    return (
        <AdminShell>
            <div className="animate-fade-up" data-testid="marketing-pages-page">
                <div className="ddi-eyebrow text-[#0F766E]">Super Admin · Marketing CMS</div>
                <h1 className="mt-1.5 text-3xl md:text-[34px] font-bold text-[#123B5D] tracking-tight leading-[1.1]">
                    Marketing Pages &amp; Demo Videos
                </h1>
                <p className="mt-2 text-sm text-[#17212B]/70 max-w-2xl">
                    Every marketing / footer page is stored as an editable Markdown row. Restore to
                    seeded defaults any time. Every save is audit-logged.
                </p>

                <div className="mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
                    <div className="lg:col-span-4">
                        <div className="ddi-card p-4" data-testid="pages-list">
                            <div className="text-xs uppercase tracking-widest text-[#17212B]/50 font-semibold mb-3">
                                Pages ({pages.length})
                            </div>
                            {Object.keys(grouped).sort().map((cat) => (
                                <div key={cat} className="mb-4">
                                    <div className="text-[11px] font-bold uppercase tracking-wider text-[#123B5D]/70 mb-1.5">
                                        {CATEGORY_LABEL[cat] || cat}
                                    </div>
                                    <ul className="space-y-1">
                                        {grouped[cat].map((p) => (
                                            <li key={p.key}>
                                                <button
                                                    onClick={() => pickPage(p.key)}
                                                    className={
                                                        "w-full text-left text-[13px] py-1.5 px-2 rounded transition-colors " +
                                                        (selKey === p.key
                                                            ? "bg-[#123B5D]/10 text-[#123B5D] font-semibold"
                                                            : "text-[#17212B]/80 hover:bg-[#F5F8FA]")
                                                    }
                                                    data-testid={`pages-item-${p.key}`}
                                                >
                                                    <span className="truncate block">{p.title}</span>
                                                    <span className="text-[10px] text-[#17212B]/50">/p/{p.key}</span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            ))}
                        </div>
                    </div>

                    <div className="lg:col-span-8">
                        {!draft ? (
                            <div className="ddi-card p-8 text-center text-sm text-[#17212B]/60">
                                Select a page on the left to edit its content.
                            </div>
                        ) : (
                            <div className="ddi-card p-6" data-testid="page-editor">
                                <div className="flex items-center justify-between gap-3 mb-4">
                                    <div className="min-w-0">
                                        <div className="text-[11px] uppercase tracking-widest text-[#17212B]/50">
                                            Editing · /p/{draft.key}
                                        </div>
                                        <div className="mt-0.5 text-lg font-bold text-[#123B5D] truncate">{draft.title}</div>
                                    </div>
                                    <a href={`/p/${draft.key}`} target="_blank" rel="noreferrer"
                                        className="text-xs text-[#2563EB] font-semibold hover:underline inline-flex items-center gap-1">
                                        Preview <ExternalLink size={11} />
                                    </a>
                                </div>
                                <div className="space-y-4">
                                    <div>
                                        <label className="ddi-input-label">Title</label>
                                        <input className="ddi-input" value={draft.title}
                                            onChange={(e) => setDraft({ ...draft, title: e.target.value })}
                                            data-testid="page-title" />
                                    </div>
                                    <div>
                                        <label className="ddi-input-label">Meta description (SEO)</label>
                                        <input className="ddi-input" value={draft.meta_description || ""}
                                            onChange={(e) => setDraft({ ...draft, meta_description: e.target.value })}
                                            data-testid="page-meta" />
                                    </div>
                                    <div>
                                        <label className="ddi-input-label">Body (Markdown)</label>
                                        <textarea className="ddi-input font-mono text-[12px] leading-relaxed"
                                            rows={20} value={draft.body_markdown || ""}
                                            onChange={(e) => setDraft({ ...draft, body_markdown: e.target.value })}
                                            data-testid="page-body" />
                                    </div>
                                    <label className="inline-flex items-center gap-2 text-sm">
                                        <input type="checkbox" checked={!!draft.published}
                                            onChange={(e) => setDraft({ ...draft, published: e.target.checked })}
                                            data-testid="page-published" />
                                        <span className="text-[#123B5D] font-medium">Published (visible to visitors)</span>
                                    </label>
                                </div>
                                <div className="mt-6 flex gap-2 flex-wrap">
                                    <button className="ddi-teal-btn text-sm" disabled={busy}
                                        onClick={savePage} data-testid="page-save">
                                        <Save size={13} className="mr-1.5" />
                                        {busy ? "Saving…" : "Save changes"}
                                    </button>
                                    <button className="ddi-btn-outline text-sm" disabled={busy}
                                        onClick={restorePage} data-testid="page-restore">
                                        <RotateCcw size={13} className="mr-1.5" />
                                        Restore defaults
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* ── Demo Videos ─────────────────────────────────────── */}
                <section className="mt-10" data-testid="demo-videos-section">
                    <div className="flex items-center gap-2">
                        <Youtube size={20} className="text-[#DC2626]" />
                        <h2 className="text-xl font-bold text-[#123B5D]">Demo Videos</h2>
                    </div>
                    <p className="mt-1 text-sm text-[#17212B]/70">Shown on /demos and in relevant CMS pages.</p>

                    <div className="mt-4 ddi-card p-5">
                        <div className="text-xs uppercase tracking-widest text-[#17212B]/50 font-semibold mb-3">Add a video</div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                                <label className="ddi-input-label">Title</label>
                                <input className="ddi-input" value={vDraft.title}
                                    onChange={(e) => setVDraft({ ...vDraft, title: e.target.value })}
                                    placeholder="How Doctor Digital Identity Works" data-testid="video-title" />
                            </div>
                            <div>
                                <label className="ddi-input-label">YouTube URL</label>
                                <input className="ddi-input" value={vDraft.youtube_url}
                                    onChange={(e) => setVDraft({ ...vDraft, youtube_url: e.target.value })}
                                    placeholder="https://www.youtube.com/watch?v=…" data-testid="video-url" />
                            </div>
                            <div>
                                <label className="ddi-input-label">Category</label>
                                <select className="ddi-input" value={vDraft.category}
                                    onChange={(e) => setVDraft({ ...vDraft, category: e.target.value })}
                                    data-testid="video-category">
                                    {DEMO_VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                            </div>
                            <div>
                                <label className="ddi-input-label">Sort order</label>
                                <input type="number" className="ddi-input" value={vDraft.sort_order}
                                    onChange={(e) => setVDraft({ ...vDraft, sort_order: parseInt(e.target.value || 0, 10) })}
                                    data-testid="video-order" />
                            </div>
                            <div className="md:col-span-2">
                                <label className="ddi-input-label">Description (optional)</label>
                                <textarea className="ddi-input" rows={2} value={vDraft.description}
                                    onChange={(e) => setVDraft({ ...vDraft, description: e.target.value })}
                                    data-testid="video-description" />
                            </div>
                        </div>
                        <div className="mt-3">
                            <button className="ddi-teal-btn text-sm" disabled={vBusy} onClick={createVideo} data-testid="video-create">
                                <Plus size={13} className="mr-1.5" /> {vBusy ? "Adding…" : "Add video"}
                            </button>
                        </div>
                    </div>

                    <div className="mt-4 space-y-2" data-testid="videos-list">
                        {videos.length === 0 && (
                            <div className="text-sm text-[#17212B]/60">No videos yet.</div>
                        )}
                        {videos.map((v) => (
                            <div key={v.id} className="ddi-card p-4 flex items-center gap-4"
                                data-testid={`video-row-${v.id}`}>
                                <div className="w-24 h-14 bg-[#0F1B2D] rounded overflow-hidden shrink-0">
                                    {v.youtube_id && (
                                        <img alt="" src={`https://img.youtube.com/vi/${v.youtube_id}/mqdefault.jpg`}
                                            className="w-full h-full object-cover" />
                                    )}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="font-semibold text-[#123B5D] text-sm truncate">{v.title}</div>
                                    <div className="text-[11px] text-[#17212B]/60 truncate">
                                        <span className="uppercase tracking-wider">{v.category}</span> · {v.youtube_url}
                                    </div>
                                </div>
                                <select className="ddi-input !w-auto text-[12px]" value={v.category}
                                    onChange={(e) => updateVideo(v.id, { category: e.target.value })}
                                    data-testid={`video-cat-${v.id}`}>
                                    {DEMO_VIDEO_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <label className="inline-flex items-center gap-1 text-[12px]">
                                    <input type="checkbox" checked={!!v.published}
                                        onChange={(e) => updateVideo(v.id, { published: e.target.checked })}
                                        data-testid={`video-pub-${v.id}`} />
                                    Published
                                </label>
                                <button className="text-[#DC2626]" onClick={() => deleteVideo(v.id)}
                                    data-testid={`video-del-${v.id}`} title="Delete">
                                    <Trash2 size={15} />
                                </button>
                            </div>
                        ))}
                    </div>
                </section>
            </div>
        </AdminShell>
    );
}
